import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/cars-for-sale", label: "For Sale" },
  { to: "/cars-for-rent", label: "For Rent" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="/logo.png"
            alt="Tassie Automotive"
            className="h-11 w-11 object-contain logo-animate"
          />
          <div className="leading-tight">
            <div className="font-display text-xl uppercase tracking-tight text-white">Tassie</div>
            <div className="font-mono text-[10px] uppercase text-brand-red tracking-[0.3em] -mt-1">Automotive</div>
          </div>
          <img
            src="/repco-badge.png"
            alt="Repco Authorised Service"
            data-testid="nav-repco-badge"
            className="h-7 sm:h-9 w-auto ml-1.5 sm:ml-2 opacity-95"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive ? "text-brand-red" : "text-zinc-300 hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user && (user.role === "admin" || user.role === "staff") ? (
            <>
              <Link
                data-testid="nav-dashboard"
                to="/admin"
                className="px-4 py-2 text-sm font-medium uppercase tracking-wider text-white hover:text-brand-red"
              >
                Admin
              </Link>
              <button
                data-testid="nav-logout"
                onClick={handleLogout}
                className="px-5 py-2 text-sm font-bold uppercase tracking-wider bg-brand-red text-white hover:bg-[#B91C2B] transition-colors"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                data-testid="nav-account"
                to="/account"
                className="hidden md:inline-flex items-center px-3 py-2 text-sm uppercase tracking-wider text-foreground/80 hover:text-brand-red transition-colors"
              >
                My Account
              </Link>
              <Link
                data-testid="nav-book"
                to="/book"
                className="glow-btn px-5 py-2 text-sm font-bold uppercase tracking-wider bg-brand-red text-white hover:bg-[#B91C2B] transition-colors rounded"
              >
                Book now
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white p-2 flex items-center gap-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/90">
          <div className="px-6 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium uppercase tracking-wider ${
                    isActive ? "text-brand-red" : "text-zinc-300"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="border-t border-white/10 my-2"></div>
            {user && (user.role === "admin" || user.role === "staff") ? (
              <>
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-dashboard"
                  className="px-3 py-2 text-sm uppercase text-white"
                >
                  Admin
                </Link>
                <button
                  onClick={() => {
                    setOpen(false);
                    handleLogout();
                  }}
                  data-testid="nav-mobile-logout"
                  className="px-3 py-2 text-sm uppercase text-left text-brand-red"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
              <Link to="/account" onClick={() => setOpen(false)} data-testid="nav-mobile-account" className="px-3 py-2 text-sm uppercase text-foreground/80">My Account</Link>
              <Link to="/book" onClick={() => setOpen(false)} data-testid="nav-mobile-book" className="px-3 py-2 text-sm uppercase text-brand-red font-bold">Book now</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
